<template>
    <div id="app" class="d-flex flex-column h-100">
        <div class="outer">
            <div v-if="loading" class="container results" style="padding-top: 1rem;">
                <!-- SKELETON COMPONENTS -->
                <div class="header">
                    <div class="store">
                        <Skeleton shape="circle" width="15rem" height="15rem" />
                        <Skeleton width="60%" height="2rem" class="mb-2" />
                        <Skeleton width="80%" height="1rem" class="mb-1" />
                        <Skeleton width="50%" height="1rem" />
                    </div>

                    <div class="headertitle small">
                        <Skeleton width="15rem" height="2rem" class="mb-2"/>
                        <Skeleton width="30%" height="1rem" />
                    </div>
                </div>
                <!-- === CONTENT SKELETON === -->
                <div class="content">

                    <!-- Header -->
                    <div class="headertitle large">
                        <Skeleton width="15rem" height="2rem" class="mb-2"/>
                        <Skeleton width="10rem" height="1rem" class="mt-2" />
                    </div>

                    <!-- Labels -->
                    <div class="labels">
                        <Skeleton width="20rem" class="mb-2" />
                        <Skeleton width="18rem" class="mb-2" />
                        <Skeleton width="15rem" class="mb-2" />
                        <Skeleton width="17rem" class="mb-2" />
                    </div>

                    <!-- Summary -->
                    <div class="p-datatable p-component p-datatable-responsive-stack p-datatable-striped" data-scrollselectors=".p-datatable-wrapper">
                        <h4><Skeleton width="12rem" /></h4>

                        <div class="p-datatable-wrapper summary" style="overflow: hidden">
                            <div class="content md:grid-cols-2 gap-6">
                                <Skeleton shape="circle" width="200px" height="200px" />
                                <div class="table">
                                    <Skeleton width="100%" height="2rem" class="mb-2" />
                                    <Skeleton width="100%" height="2rem" class="mb-2" />
                                    <Skeleton width="100%" height="2rem" class="mb-2" />
                                </div>
                            </div>
                        </div>

                        <!-- Results -->
                        <div class="p-datatable-wrapper resultitems">
                            <Skeleton width="15rem" height="2rem" class="mb-2"/>
                            <div class="flex flex-col gap-3">
                                <Skeleton width="100%" height="15rem" />
                            </div>
                        </div>
                    </div>
                </div>
                <!-- END SKELETON COMPONENTS -->
            </div>
            <div v-else class="container results" style="padding-top: 1rem;">
                
                <div class="header">
                    <div class="store">
                        <div class="shop-logo">
                            <img :src="`data:image/*;base64,${data.Company.Logo}`" alt="Shop Logo" width="600" />
                        </div>
                        <div class="shop-info">
                            <h1>{{ data.Company.CompanyName }}</h1>
                            <p>{{ data.Company.Address }}</p>
                            <p><a :href="`tel:${data.Company.ContactNumber}`">{{ data.Company.ContactNumber }}</a>, <a :href="`mailto:${data.Company.Email}`">{{ data.Company.Email }}</a></p>
                            
                        </div>
                    </div>
                    <div class="headertitle small">
                        <h1>INSPECTION RESULT</h1>
                        <p v-if="data.ScheduledTime"><strong>Date:</strong> {{ ScheduledTimeComputed }}</p>
                    </div>
                </div>

                <div class="content">

                    <div class="headertitle large">
                        <h1>INSPECTION RESULT</h1>
                        <p v-if="data.ScheduledTime"><strong>Date:</strong> {{ ScheduledTimeComputed }}</p>
                    </div>

                    <div class="labels">
                        <!-- <p class="date"><span><strong>Date of Inspection:</strong> September 1, 2025</span></p> -->
                        <p class="customer"><span><strong>Customer:</strong> {{ data.FirstName }}&nbsp;{{ data.LastName }}</span></p>
                        <p class="vehicle"><span><strong>Vehicle:</strong> {{ data.Color }}&nbsp;{{ data.Make }}&nbsp;{{ data.Model }}</span> <span><strong>Plate #:</strong> {{ data.Plate }}</span></p>
                        <p class="resources"><span><strong>Workorder: </strong> {{ data.WorkOrderNumber }}</span> <span><strong>Odometer: </strong> {{ UsageComputed }}</span></p>
                        <p class="resources"><span><strong>Service Advisor:</strong> {{ data.ServiceAdvisor }}</span> <span><strong>Technician:</strong> {{data.Technician}}</span></p>
                    </div>

                    <div class="p-datatable p-component p-datatable-responsive-stack p-datatable-striped" data-scrollselectors=".p-datatable-wrapper">

                        <h4>
                            <strong>{{ data?.ServicePackage?.WorkOrderPackageHeader }} Summary</strong>
                        </h4>

                        <div class="p-datatable-wrapper summary" v-if="dataByResult">

                            <div class="content">

                                <div class="chart">
                                    <Chart type="doughnut" :data="chartData" :options="chartOptions" />
                                    <p class="center value cond">{{ lineItemCount }} points</p>
                                </div>

                                <div class="table">
                                    <DataTable :value="dataByResult" breakpoint="360px" class="resultsTable">
                                        <Column field="result" header="Result">
                                            <template #body="{ data }">
                                                <!-- <span :class="`value_${data?.result.toLowerCase()}`">{{ data?.result }}</span> -->
                                                <span :class="`customer-badge status-${data?.result.toLowerCase()}`"><a :href="`#${data?.result}`">{{ data?.result }}</a></span>
                                            </template>
                                        </Column>
                                        <Column field="count" header="Count"></Column>
                                        <Column field="percentage" header="Percentage">
                                            <template #body="{ data }">
                                                {{ `${data.percentage.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` }}
                                            </template>
                                        </Column>
                                    </DataTable>
                                </div>

                            </div>

                        </div><!-- Chart & Summary -->

                        <div v-if="data?.ServicePackage" class="p-datatable-wrapper resultitems">

                            <h4><strong>Inspection Results</strong></h4>

                            <Panel v-for="(value, key) in parsedItems"
                                v-model:collapsed="collapsedStates[key]"
                                toggleable
                                :key="key"
                                :class="value.length === 0 ? 'empty-panel' : ''"
                            >
                                <template #header>
                                    <div class="flex items-center gap-2">
                                        <Badge :value="value.length > 0 ? value.length : '0'" />
                                        <a :id="key" :name="key"></a><span :class="'customer-badge status-' + key">{{ key }}</span>
                                    </div>
                                </template>
                                <ResultTable :items="value" />
                            </Panel>

                        </div><!-- Results Tables -->

                    </div>

                </div><!-- content -->

            </div>

            <div class="container footer">
                <div class="headertitle small">
                    <h4><strong>Questions about your inspection results?</strong></h4>
                    <a :href="`tel:${data.Company.ContactNumber}`"><Button label="Call Shop" link class="p-button-rounded btn-primary mr-2" /></a>
                    <a :href="`mailto:${data.Company.Email}?subject=${encodeURIComponent(`Inspection questions for work order #${data.WorkOrderNumber}`)}.`"><Button label="Email Us" link class="p-button-rounded btn-primary mr-2" /></a>
                    <a href="#" target="_blank"><Button label="Visit Website" link class="p-button-rounded btn-primary" /></a>
                    <p style="margin: 1rem 0 0 0;">{{ data.CTA }}</p>
                </div>
            </div>

        </div>
    </div>

</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import Chart from 'primevue/chart';
import ResultTable from '@/components/ResultTable.vue';
import moment from 'moment';

const route = useRoute();
const router = useRouter();
const id = route.query.id ?? '';
interface WorkOrderPackageInspectionLinesItem {
  Rank: string
  WorkOrderPackageInspectionLineID: string
  Title: string
  InputType: string
  Result: string
  Comment: string
  Notes: string
  ShopNotes: string
  Type: string
  LF: string
  RF: string
  LR: string
  RR: string
  UnitText: string
  Unit: string
  MediaKeys: string[]
}

const data = ref({
  FirstName: '',
  LastName: '',
  WorkOrderNumber: 0,
  Make: '',
  Model: '',
  Color: '',
  Plate: '',
  Technician: '',
  ServiceAdvisor: '',
  InUsage: 0,
  ServicePackage: {
    WorkOrderPackageHeader: '',
    WorkOrderPackageInspectionLines: [] as WorkOrderPackageInspectionLinesItem[]
  },
  Company: {
    Logo: '',
    CompanyName: '',
    ContactNumber: '',
    Email: '',
    Address: ''
  },
  ScheduledTime: '',
  CTA: ''
});

type ResultCategory = 'Required' | 'Suggested' | 'OK';
const parsedItems = ref<Record<ResultCategory, WorkOrderPackageInspectionLinesItem[]>>({
    Required: [],
    Suggested: [],
    OK: []
});

const dataByResult = ref();
const UsageComputed = computed(() => new Intl.NumberFormat().format(data.value.InUsage));
const ScheduledTimeComputed = computed(() => moment(data.value.ScheduledTime).format('LL'))
const chartData = ref();
const chartOptions = ref();
const loading = ref(true);
const lineItemCount = ref(0);

const collapsedStates = ref<Record<ResultCategory, boolean>>({
    Required: false,
    Suggested: false,
    OK: true
});

onMounted(async () => {
    await getDVIResultByID();
    const packages = data.value?.ServicePackage?.WorkOrderPackageInspectionLines;
    if (!packages || packages.length === 0) { // for missing ServicePackage or 0 line items
        return router.push('/');
    }
    parseDVIResultData();
    chartData.value = setChartData();
    chartOptions.value = setChartOptions();
    loading.value = false;
});

const setChartData = () => {
    const documentStyle = getComputedStyle(document.body);

    return {
        labels: dataByResult.value.map(item => item.result),
        datasets: [
            {
                data: dataByResult.value.map(item => item.count),
                backgroundColor: ['hsl(0deg, 65%, 72%)', documentStyle.getPropertyValue('--yellow-500'), documentStyle.getPropertyValue('--green-500')],
                hoverBackgroundColor: ['hsl(0deg, 70%, 77%)', documentStyle.getPropertyValue('--yellow-400'), documentStyle.getPropertyValue('--green-400')]
            }
        ]
    };
};

const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');

    return {
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true,
                    color: textColor
                }
            }
        }
    };
};

const parseDVIResultData = () => {
    const packages = data.value?.ServicePackage?.WorkOrderPackageInspectionLines;
    const lineItems = packages.filter((item) => item.Type === 'Line');
    lineItemCount.value = lineItems.length;

    for (const line of lineItems) {
        switch (line.Result) {
            case 'Required':
                parsedItems.value.Required.push(line)
                break;
            case 'Suggested':
                parsedItems.value.Suggested.push(line)
                break;
            case 'OK':
                parsedItems.value.OK.push(line)
                break;
            default: break;
        }
    }

    dataByResult.value = (Object.keys(parsedItems.value) as ResultCategory[]).map(key => ({
        result: key,
        count: parsedItems.value[key].length,
        percentage: (parsedItems.value[key].length / lineItems.length) * 100
    }));
}

const getDVIResultByID = async () => {
    try {
        const response = await fetch(`${process.env.VUE_APP_NODEJS_BASE_URL}dviworkorders/customerreport/v2/${id}`);
        const result = await response.json();
        data.value = result.message;
    } catch (e) {
        router.push('/')
    }
}
</script>

<!-- <script>
    document.querySelectorAll('.dataResult').forEach(td => {
      const nodes = td.childNodes;
      nodes.forEach(node => {
        if (node.nodeType === 3 && node.textContent.trim()) {
          node.parentElement.style.color = 'red';
        }
      });
    });
</script> -->

<style>
.empty-panel .p-panel-content {
  display: none;
}
.empty-panel .p-panel-header-icon {
  pointer-events: none;
  opacity: 0.5;
}
@media screen and (min-width: 1280px) {

    .outer {
        max-width: 80rem;
        margin: 0 auto;
    }
    
}
</style>